import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const badge = (s) => ({ Pending: 'bg-gold/15 text-gold-dark', Approved: 'bg-forest/10 text-forest', Scheduled: 'bg-forest/10 text-forest', Paid: 'bg-forest text-cream', 'Payment Issue': 'bg-red-50 text-red-600' }[s] || 'bg-dark/10 text-dark');
const STATUSES = ['Approved', 'Scheduled', 'Paid', 'Payment Issue'];

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ status: 'Paid', payment_date: '', method: '', reference: '', notes: '' });

  const load = useCallback(() => api.get('/admin/payments').then((r) => setPayments(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const open = (p) => { setSel(p); setForm({ status: p.status === 'Pending' ? 'Paid' : p.status, payment_date: p.payment_date || '', method: p.method || '', reference: p.reference || '', notes: p.notes || '' }); };
  const record = async () => { await api.post(`/admin/payments/${sel.payment_id}/record`, form); toast.success('Payment updated'); setSel(null); load(); };

  return (
    <div data-testid="admin-payments">
      <p className="font-manrope text-dark/60 mb-6">Farm payment ledger. Record manual payments now; the architecture supports automated ACH payouts later.</p>
      <div className="bg-white rounded-2xl border border-forest/10 overflow-hidden">
        {payments.length === 0 ? <p className="p-12 text-center font-manrope text-dark/50">No payment records yet.</p> : (
          <table className="w-full text-left font-manrope text-sm">
            <thead className="bg-cream text-dark/60"><tr>{['Farm', 'Order', 'Amount', 'Status', 'Date', ''].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-forest/10">
              {payments.map((p) => (
                <tr key={p.payment_id} data-testid={`admin-payment-${p.payment_id}`}>
                  <td className="px-4 py-3 font-medium text-dark">{p.farm_name}</td>
                  <td className="px-4 py-3 text-dark/70">{p.order_id}</td>
                  <td className="px-4 py-3 font-semibold text-dark">${p.total.toFixed(2)}</td>
                  <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-bold ${badge(p.status)}`}>{p.status}</span></td>
                  <td className="px-4 py-3 text-dark/70">{p.payment_date || '—'}</td>
                  <td className="px-4 py-3"><button onClick={() => open(p)} className="text-forest font-medium hover:underline" data-testid={`record-${p.payment_id}`}>Record</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="bg-cream max-w-md">
          {sel && (<>
            <DialogHeader><DialogTitle className="font-playfair text-2xl">Record Payment</DialogTitle><DialogDescription>Log payment status, method and reference for this farm.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <p className="font-manrope text-sm text-dark/70">{sel.farm_name} · Order {sel.order_id} · <strong>${sel.total.toFixed(2)}</strong></p>
              <div><Label>Status</Label><select className="w-full bg-white border border-forest/20 rounded-xl px-4 py-2.5 font-manrope" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="payment-status">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
              <div><Label>Payment date</Label><Input type="date" className="bg-white border-forest/20" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} /></div>
              <div><Label>Method</Label><Input className="bg-white border-forest/20" placeholder="Check, ACH, Cash" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} /></div>
              <div><Label>Reference / check #</Label><Input className="bg-white border-forest/20" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
              <div><Label>Notes</Label><Textarea className="bg-white border-forest/20" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <button onClick={record} className="btn-primary w-full justify-center" data-testid="save-payment">Save</button>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
